// The page's side of the engine: starts the Worker, sends requests and resolves each with its
// answer, and passes progress and autosave notices to whoever listens.

import type { Message, Op, Progress, Request, Requests, StoreAsk, StoreReply } from './protocol.ts'

declare const __BUILD__: string

type Pending = { resolve: (value: unknown) => void; reject: (error: Error) => void }

export type AutosaveNotice = { savedAt: number } | { error: string }

/// Where the autosave's files go when not in the origin private file system: the desktop app's
/// data folder (brief 46).
export type Store = (op: StoreAsk['op'], file: string, text?: string) => Promise<string | null>

export class Engine {
  private worker: Worker
  private next = 1
  private pending = new Map<number, Pending>()
  onProgress: (p: Progress) => void = () => {}
  onAutosave: (n: AutosaveNotice) => void = () => {}

  constructor(store: Store | null = null) {
    this.worker = new Worker(new URL(`worker.js?v=${__BUILD__}${store ? '&store=app' : ''}`, import.meta.url), { type: 'module' })
    this.worker.onmessage = (event: MessageEvent<Message>) => {
      const m = event.data
      if ('progress' in m) return this.onProgress(m.progress)
      if ('autosave' in m) return this.onAutosave(m.autosave)
      if ('store' in m) {
        const { id, op, file, text } = m.store
        const answer = (reply: StoreReply['storeReply']) => this.worker.postMessage({ storeReply: reply } satisfies StoreReply)
        void (store ?? (() => Promise.reject(new Error('no store'))))(op, file, text).then(value => answer({ id, ok: true, value }), (e: unknown) => answer({ id, ok: false, error: e instanceof Error ? e.message : String(e) }))
        return
      }
      const p = this.pending.get(m.id)
      if (!p) return
      this.pending.delete(m.id)
      if (m.ok) p.resolve(m.value)
      else p.reject(new Error(m.error))
    }
    this.worker.onerror = event => {
      const error = new Error(`the engine stopped: ${event.message || 'unknown error'}`)
      for (const p of this.pending.values()) p.reject(error)
      this.pending.clear()
    }
  }

  /// Sends one request. Buffers in `transfer` move to the Worker instead of being copied.
  request<K extends Op>(op: K, req: Requests[K]['req'], transfer: Transferable[] = []): Promise<Requests[K]['res']> {
    const id = this.next++
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject })
      this.worker.postMessage({ id, op, ...req } as Request, transfer)
    })
  }
}
